import { useNavigate } from 'react-router-dom';
import React from 'react';
import useNeveForm from '../../components/forms/NeveForm';
import strings from '../../strings/strings.json';
import { MainSectionWithGaps } from '../../components/sections/MainSection';
import { FormCard } from '../../components/forms/FormComponents';
import {
    createErrorMessage,
    createGlobalErrorFromAPIError,
    globalErrorCodes,
    requiredFieldErrorMessage,
} from '../../shared/error-messages';
import TextField from '../../components/forms/TextField';
import { ButtonRowRight } from '../../components/Elements';
import useModifyRequest from '../../hooks/useModifyRequest';
import { logError } from '../../lib/debug-helpers';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import Header from 'components/sections/Header';
import NavBackButton from 'components/buttons/NavBackButton';
import Button from 'components/buttons/Button';

type AddUserFormInput = {
    firstName: string;
    lastName: string;
    emailAddress: string;
};

export default function AddUserPage(): JSX.Element {
    const navigate = useNavigate();

    const [errorMessage, setErrorMessage] = React.useState<string>('');

    const { modifyData: addUser, loading: saving } = useModifyRequest(`admin/users`, 'POST');

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
    } = useNeveForm<AddUserFormInput>({
        firstName: '',
        lastName: '',
        emailAddress: '',
    });

    async function onSubmit(formData: AddUserFormInput) {
        const newUserResponse = await addUser({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.emailAddress,
        });

        if (newUserResponse.errors) {
            const apiErrorMessage = createGlobalErrorFromAPIError(newUserResponse.errors[0].code);

            logError(apiErrorMessage);

            if (apiErrorMessage === globalErrorCodes.unknownError) {
                setErrorMessage(strings.addUserPage.genericError);
            } else {
                setErrorMessage(apiErrorMessage);
            }

            return;
        }

        navigate('/admin/dashboard', { state: { addUserSuccess: true } });
    }

    return (
        <>
            <Header
                title={strings.addUserPage.heading}
                aboveTitleElement={
                    <NavBackButton
                        backButtonTitle={strings.addUserPage.backButton.title}
                        backButtonAria={strings.addUserPage.backButton.aria}
                        backButtonHref={'/admin/dashboard'}
                    />
                }
            />
            <MainSectionWithGaps>
                <FormCard onSubmit={handleSubmit(onSubmit)}>
                    <TextField
                        fieldName={'firstName'}
                        labelText={strings.addUserPage.fields.firstName.title}
                        inputAria={strings.addUserPage.fields.firstName.aria}
                        errorMessage={errors.firstName?.message}
                        required
                        width={'480px'}
                        {...register('firstName', {
                            required: {
                                value: true,
                                message: requiredFieldErrorMessage(strings.addUserPage.fields.firstName.title),
                            },
                        })}
                    />
                    <TextField
                        fieldName={'lastName'}
                        labelText={strings.addUserPage.fields.lastName.title}
                        inputAria={strings.addUserPage.fields.lastName.aria}
                        errorMessage={errors.lastName?.message}
                        required
                        width={'480px'}
                        {...register('lastName', {
                            required: {
                                value: true,
                                message: requiredFieldErrorMessage(strings.addUserPage.fields.lastName.title),
                            },
                        })}
                    />
                    <TextField
                        fieldName={'emailAddress'}
                        labelText={strings.addUserPage.fields.emailAddress.title}
                        inputAria={strings.addUserPage.fields.emailAddress.aria}
                        errorMessage={errors.emailAddress?.message}
                        required
                        width={'480px'}
                        {...register('emailAddress', {
                            required: {
                                value: true,
                                message: requiredFieldErrorMessage(strings.addUserPage.fields.emailAddress.title),
                            },
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: createErrorMessage(
                                    strings.addUserPage.fields.emailAddress.title,
                                    strings.errorMessages.fieldErrorInvalidEmail,
                                ),
                            },
                        })}
                    />
                    <ButtonRowRight>
                        <Button
                            type="submit"
                            title={strings.addUserPage.submitButton.title}
                            aria={strings.addUserPage.submitButton.aria}
                            disabled={saving}
                        />
                    </ButtonRowRight>
                    {!isDirty && errorMessage && (
                        <>
                            <Notification notificationType={NotificationType.DANGER} title={errorMessage} />
                        </>
                    )}
                </FormCard>
            </MainSectionWithGaps>
        </>
    );
}
